<template>
  <modal-wrapper
    ref="modalRef"
    :content-overflow-y="'visible'"
    :header="$t('projectDetails.contentTypes.addContentType')"
    @ok-button-click="handleModalOK"
    @cancel-button-click="handleModalCancel"
    @hide="handleModalCancel"
    @show="onModalShow"
  >
    <div id="select-container">
      <styled-multiselect
        v-model="selectedContentType"
        :options="allContentTypes ?? []"
        :placeholder="$t('projectDetails.contentTypes.selectContentType')"
        label="name"
        track-by="name"
        :loading="isLoadingContentTypes"
      ></styled-multiselect>

      <div id="spinner-container">
        <spinner-wrapper
          v-if="isSavingContentType"
          id="spinner"
          :size="SpinnerSize.ML"
        />
      </div>
    </div>

    <div id="error-messages">
      <p v-if="contentTypeErrorMessage" class="error-message">
        {{ contentTypeErrorMessage }}
      </p>
      <p v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </p>
    </div>
  </modal-wrapper>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import axios, { AxiosError, AxiosResponse } from 'axios';
  import { UpdateWriteOpResult } from 'mongoose';
  import {
    IAddDigitalizationProjectContentTypeAssignmentDto,
    IContentType,
    IContentTypeNameAndId,
    ProjectType,
    Serialized,
  } from 'types';
  import { useI18n } from 'vue-i18n';
  import StyledMultiselect from '../../components/StyledMultiselect/StyledMultiselect.vue';
  import endpointsList from '../../helpers/endpointsList';
  import { useToast } from '../../composables/useToast';
  import { useModalWrapper } from '../ModalWrapper';
  import { SpinnerSize } from '../SpinnerWrapper/SpinnerWrapper.types';

  const { t } = useI18n();
  const { createErrorToast } = useToast();

  const { modalRef, show, hide } = useModalWrapper();
  defineExpose({ show, hide });

  const emit = defineEmits<{
    (e: 'content-type-added'): void;
  }>();

  const props = defineProps<{
    projectId: string;
    supplierId?: string;
    mode: ProjectType;
  }>();

  const allContentTypes = ref<IContentTypeNameAndId[]>([]);
  const selectedContentType = ref<IContentTypeNameAndId | null>(null);
  const isLoadingContentTypes = ref(false);
  const isSavingContentType = ref(false);
  const contentTypeErrorMessage = ref<null | string>(null);
  const errorMessage = ref<null | string>(null);

  function cleanAfterAddingContentType() {
    selectedContentType.value = null;
    isLoadingContentTypes.value = false;
    isSavingContentType.value = false;
    resetErrors();
  }

  function resetErrors() {
    errorMessage.value = null;
    contentTypeErrorMessage.value = null;
  }

  async function handleModalOK() {
    resetErrors();

    if (!selectedContentType.value) {
      contentTypeErrorMessage.value = t(
        'projectDetails.contentTypes.errors.noContentTypeSelected',
      );
    }
    if (!selectedContentType.value) {
      return;
    }

    isSavingContentType.value = true;

    try {
      if (props.mode === ProjectType.SUPPLIER_PROJECT) {
        await submitProjectContentType();
      } else if (props.mode === ProjectType.DIGITALIZATION_PROJECT) {
        await submitDigitalizationProjectContentTypeMapping();
      }
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        if (err.request.status === 409) {
          errorMessage.value = t('projectDetails.contentTypeAlreadyAdded');
        } else {
          errorMessage.value = t('suppliersForm.submitError');
        }
      } else {
        errorMessage.value = t('suppliersForm.submitError');
      }
    } finally {
      isSavingContentType.value = false;
    }
  }

  async function submitProjectContentType() {
    if (!selectedContentType.value) return;

    const assignmentObject = {
      supplierId: props.supplierId,
      projectId: props.projectId,
      fieldsMapping: [],
    };

    const response: AxiosResponse<IContentType | UpdateWriteOpResult> =
      await axios.post(
        endpointsList.contentTypeMappings.addAssignmentToContentType(
          selectedContentType.value.id,
        ),
        assignmentObject,
      );

    if (response.status === 201) {
      isSavingContentType.value = false;
      hide();
      cleanAfterAddingContentType();
      emit('content-type-added');
    }
  }

  async function submitDigitalizationProjectContentTypeMapping() {
    if (!selectedContentType.value) return;

    const body: Serialized<IAddDigitalizationProjectContentTypeAssignmentDto> =
      {
        contentTypeId: selectedContentType.value.id,
        digitalizationProjectId: props.projectId,
      };

    await axios.post(
      endpointsList.contentTypeMappings
        .addDigitalizationProjectContentTypeAssignment,
      body,
    );

    isSavingContentType.value = false;
    hide();
    cleanAfterAddingContentType();
    emit('content-type-added');
  }

  function handleModalCancel() {
    cleanAfterAddingContentType();
  }

  async function getAllContentTypes() {
    isLoadingContentTypes.value = true;
    try {
      const response: AxiosResponse<IContentTypeNameAndId[]> = await axios.get(
        endpointsList.contentTypeMappings.getAvailableContentTypes,
      );

      allContentTypes.value = response.data;
    } catch {
      createErrorToast(t('errors.error'));
    }
    isLoadingContentTypes.value = false;
  }

  function onModalShow() {
    getAllContentTypes();
  }
</script>

<style lang="scss" scoped>
  #select-container {
    display: flex;
    flex-direction: column;
    row-gap: 20px;
  }

  #spinner-container {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  #error-messages {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    p {
      font-size: 16px;
      line-height: 23px;
      color: red;
      margin-bottom: 0;
    }
  }
</style>
